import React, { useRef, useState, useEffect } from "react";
import "../styles/About.css";

const useVisible = (threshold = 0.12) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const PILLARS = [
  {
    num: "01",
    q: "Who is Codix?",
    a: "Codix is a full-service digital agency that crafts modern, fast, and reliable digital products — from corporate websites to complex SaaS platforms. We focus on clean architecture, smart development, and measurable performance.",
  },
  {
    num: "02",
    q: "Who do we help?",
    a: "We partner with businesses, startups, personal brands, freelancers, and service providers who want a strong digital presence. Whether launching, growing, or scaling — we turn your vision into a product that converts.",
  },
  {
    num: "03",
    q: "Why Codix?",
    a: "Because we obsess over results, not aesthetics alone. Every product we ship is tailored to your brand, optimised for speed, and backed by SEO, hosting, and ongoing support. We build things that grow with your business.",
  },
];

const VALUES = [
  { icon: "⚡", label: "Performance First" },
  { icon: "🎯", label: "Results Driven" },
  { icon: "🔒", label: "Secure & Reliable" },
  { icon: "🤝", label: "Long-Term Partnership" },
];

function About() {
  const [headerRef, headerVis] = useVisible(0.1);
  const [pillarsRef, pillarsVis] = useVisible(0.08);
  const [valuesRef, valuesVis] = useVisible(0.15);

  return (
    <section id="about" className="about-section">
      <div className="about-rule" aria-hidden="true" />
      <div className="about-container">

        {/* Header */}
        <div ref={headerRef} className={`about-header ${headerVis ? "about-header--in" : ""}`}>
          <span className="cdx-label">// 01 — about us</span>
          <h2 className="cdx-title">
            &lt;<span className="orange"> About</span> Codix<span className="orange">/</span>&gt;
          </h2>
          <div className="cdx-rule" />
          <p className="about-lead">
            We are a team of developers, designers, and strategists who believe
            that a great digital product is the most powerful growth tool any
            business can have.
          </p>
        </div>

        {/* Pillars */}
        <div ref={pillarsRef} className="about-pillars">
          {PILLARS.map((p, i) => (
            <div
              key={p.num}
              className={`pillar-card ${pillarsVis ? "pillar-card--in" : ""}`}
              style={{ transitionDelay: `${i * 130}ms` }}
            >
              <div className="pillar-top">
                <span className="pillar-num">{p.num}</span>
                <div className="pillar-dot" aria-hidden="true" />
              </div>
              <h3 className="pillar-q">{p.q}</h3>
              <p className="pillar-a">{p.a}</p>
              <div className="pillar-line" aria-hidden="true" />
            </div>
          ))}
        </div>

        {/* Values */}
        <div ref={valuesRef} className={`about-values ${valuesVis ? "about-values--in" : ""}`}>
          {VALUES.map(({ icon, label }, i) => (
            <div
              key={label}
              className="value-item"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <span className="value-icon">{icon}</span>
              <span className="value-label">{label}</span>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className={`about-cta-strip ${headerVis ? "about-cta-strip--in" : ""}`}>
          <p className="about-cta-text">
            Let's build something amazing together —{" "}
            <em>your website, your growth, your success.</em>
          </p>
          <a
            href="https://book.codixlb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="cdx-btn-primary"
          >
            Start a Project
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default About;
